import { useTranslation } from "react-i18next";
import { cn, Select } from "@tellescope/design-system";
import { useMemo } from "react";
import { US_STATES, USStateCode } from "@tellescope/utils";

export interface StateSelectFieldProps {
  value?: string;
  onValueChange?: (value: string) => void;
  className?: string;
  id?: string;
  statesToExclude?: USStateCode[];
  validStates?: string[];
  placeholder?: string;
  autoFocus?: boolean;
}

export const StateSelectField = ({
  value,
  onValueChange,
  className,
  id,
  statesToExclude = [],
  validStates,
  placeholder,
  autoFocus,
}: StateSelectFieldProps) => {
  const { t } = useTranslation("forms");

  const allowedStates = useMemo(() => {
    const allowed =
      validStates && validStates.length > 0 ? US_STATES.filter((s) => validStates.includes(s.value)) : US_STATES;
    return statesToExclude.length ? allowed.filter((s) => !statesToExclude.includes(s.value)) : allowed;
  }, [validStates, statesToExclude]);

  // Labels are resolved outside the memos so a language change re-translates them on re-render.
  const stateOptions = allowedStates.map((s) => ({ value: s.value, label: s.getLabel() }));

  // Resolved against the unfiltered list so an already-saved state still displays even
  // when statesToExclude or validStates keeps it out of the selectable options.
  const selectedState = useMemo(() => US_STATES.find((option) => option.value === value), [value]);
  const selectedLabel = selectedState?.getLabel();

  return (
    <Select value={value ?? ""} onValueChange={(stateCode) => onValueChange?.(stateCode)}>
      <Select.Trigger id={id} className={cn("w-full", className)} autoFocus={autoFocus} openOnFocus>
        <Select.Value placeholder={placeholder ?? t("Select a state")}>{selectedLabel}</Select.Value>
      </Select.Trigger>
      <Select.Content searchPlaceholder={t("Search states...")}>
        {stateOptions.map((state) => (
          <Select.Item key={state.value} value={state.value} keywords={[state.value]}>
            {state.label}
          </Select.Item>
        ))}
      </Select.Content>
    </Select>
  );
};
